import { CancelToken } from 'axios';
import { Formik, Form, getIn } from 'formik';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState, Fragment } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { toast } from '@hivebrite/design-system/Toast';
import { useIsMounted } from '@hivebrite/react-hooks';

import { getInitialValues } from 'hb-react/frontoffice/apps/UpdateUserData/form/initialValues';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { Checkbox } from 'hb-react/shared/components/formik/components/inputs';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

const Container = ({ config, delayBeforeDisplay, onUpdate, onCancel }) => {
  const isMounted = useIsMounted();
  const { currentUser } = useDefaultAppContexts();
  const [displayModal, setDisplayModal] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const initialValues = getInitialValues({ attributes });

  const handleCloseModal = () => {
    setDisplayModal(false);
    onCancel?.();
  };

  // Submit Handler
  const handleSubmit = async (values) => {
    const userAttributesToUpdate = attributes.reduce(
      (res, { name, updated_value: updatedValue }) => {
        if (getIn(values, name)) {
          res[name] = updatedValue;
        }

        return res;
      },
      {},
    );

    try {
      await axios.patch(Routes.user_user_path(currentUser.id, currentUser.id), {
        user: userAttributesToUpdate,
      });

      onUpdate?.(userAttributesToUpdate);

      toast.success(__('Your data has been successfully updated'));

      isMounted() && setDisplayModal(false);
    } catch (_) {
      toast.error(__('Sorry, an error occurred'));
    }
  };

  // Fetch the list of attributes to be proposed to update.
  useEffect(() => {
    let displayTimer;
    const cancelToken = CancelToken.source();

    axios
      .get(config.getDataUrl, {
        cancelToken: cancelToken.token,
      })
      .then(({ data }) => {
        setAttributes(data);

        displayTimer = setTimeout(
          () => setDisplayModal(true),
          delayBeforeDisplay,
        );
      });

    return () => {
      cancelToken.cancel();
      clearTimeout(displayTimer);
    };
  }, []);

  if (isEmpty(attributes) || !displayModal) {
    return null;
  }

  return (
    <ModalWithTemplate
      width="large"
      onCloseModal={() => setDisplayModal(false)}
      header={__(
        'Your personal informations have been modified, do you want to save those modifications?',
      )}
      screenReadersCloseButtonText={s__(
        'Accessibility|Close save profile modifications window',
      )}
    >
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {(formik) => (
          <Form style={{ width: '100%' }}>
            <TableBox data-testid="update-user-data-modal">
              <Cell />
              <Cell font="body-semibold-asphalt">{__('Info entered')}</Cell>
              <Cell font="body-semibold-asphalt">
                {__('The current info on your profile')}
              </Cell>

              {attributes.map(
                ({
                  name,
                  label,
                  updated_human_value: updatedHumanValue,
                  user_human_value: userHumanValue,
                }) => (
                  <Fragment key={name}>
                    <Cell>
                      <Checkbox name={name} label={label} />
                    </Cell>
                    <Cell>{updatedHumanValue}</Cell>
                    <Cell>{userHumanValue}</Cell>
                  </Fragment>
                ),
              )}
            </TableBox>

            <Box mt={8} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                dataTestId="confirmation-modal-cancel"
              >
                {__('No')}
              </Button>
              <Box ml={4}>
                <Button
                  isLoading={formik.isSubmitting}
                  variant="primary"
                  type="submit"
                >
                  {__('Update selected data')}
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </ModalWithTemplate>
  );
};

Container.propTypes = {
  config: PropTypes.object.isRequired,
  delayBeforeDisplay: PropTypes.number,
  onUpdate: PropTypes.func,
  onCancel: PropTypes.func,
};

Container.defaultProps = {
  delayBeforeDisplay: 2500,
};

export default Container;

const TableBox = styled(Box)`
  display: grid;
  grid-template-columns: 34% 33% 33%;
  grid-template-rows: auto;
`;

const Cell = styled(Box).attrs({
  p: 4,
})`
  border-top: 1px solid ${({ theme }) => theme.colors.dove};
`;
